/**
 * PDF summary export for the FCCERS dashboard.
 * Uses jsPDF + jspdf-autotable (already installed).
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { DashboardObservation } from './types';
import { SUBSCALE_META } from './types';
import { computeKpis, computeSiteRankings } from './aggregations';

export function exportPdf(observations: DashboardObservation[]): void {
  const doc = new jsPDF({ orientation: 'landscape' });
  const kpis = computeKpis(observations);
  const rankings = computeSiteRankings(observations);

  // ─── Page 1: Executive summary ──────────────────────────────────────
  doc.setFillColor(31, 43, 91); // dece-navy
  doc.rect(0, 0, 297, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text('FCCERS-3 Dashboard Summary', 14, 18);
  doc.setFontSize(10);
  doc.text(`Generated ${new Date().toLocaleDateString()} | ${observations.length} observations`, 14, 28);

  doc.setTextColor(31, 43, 91);
  doc.setFontSize(14);
  doc.text('Key Performance Indicators', 14, 48);

  doc.setFontSize(11);
  const kpiLines = [
    `Total Observations: ${kpis.totalObservations}`,
    `System Average: ${kpis.systemAverage !== null ? kpis.systemAverage.toFixed(2) : 'N/A'}`,
    `Children Served: ${kpis.childrenServed}`,
    `Sites Observed: ${kpis.sitesObserved}`,
  ];
  kpiLines.forEach((line, i) => doc.text(line, 14, 58 + i * 8));

  // ─── Page 2: Site comparison table ──────────────────────────────────
  doc.addPage();
  doc.setFillColor(31, 43, 91);
  doc.rect(0, 0, 297, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text('Site Comparison', 14, 14);

  const siteHeaders = ['Site', 'Obs.', 'Avg', ...SUBSCALE_META.map((s) => s.shortName)];
  const siteRows = rankings.map((r) => [
    r.siteName,
    String(r.observationCount),
    r.average !== null ? r.average.toFixed(2) : '—',
    ...SUBSCALE_META.map((s) => {
      const v = r.subscaleAverages[s.id];
      return v !== null ? v.toFixed(2) : '—';
    }),
  ]);

  autoTable(doc, {
    startY: 28,
    head: [siteHeaders],
    body: siteRows,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [31, 43, 91] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });

  // ─── Page 3+: Full observation table ────────────────────────────────
  doc.addPage();
  doc.setFillColor(31, 43, 91);
  doc.rect(0, 0, 297, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text('All Observations', 14, 14);

  const obsHeaders = ['Date', 'Site', 'Observer', 'Children', 'Total Avg', 'Scored', 'NA'];
  const obsRows = observations.map((o) => [
    o.observationDate,
    o.siteName,
    o.observerName,
    String(o.childrenPresent),
    o.totalAverage !== null ? o.totalAverage.toFixed(2) : '—',
    String(o.scoredItems),
    String(o.naItems),
  ]);

  autoTable(doc, {
    startY: 28,
    head: [obsHeaders],
    body: obsRows,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [31, 43, 91] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });

  // ─── Footer on every page ───────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Generated ${new Date().toISOString()} · Powered by SOLVED`,
      14,
      doc.internal.pageSize.height - 8,
    );
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width - 30,
      doc.internal.pageSize.height - 8,
    );
  }

  doc.save(`fccers-dashboard-${new Date().toISOString().slice(0, 10)}.pdf`);
}
